"use client";

import { useState, useCallback } from "react";
import {
  HiTable,
  HiQuestionMarkCircle,
  HiDownload,
  HiClipboardCopy,
  HiRefresh,
  HiCheckCircle,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";

// Flatten a nested object into dot-notation keys
function flattenObject(obj: Record<string, unknown>, prefix = ""): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const nested = flattenObject(value as Record<string, unknown>, fullKey);
      Object.assign(result, nested);
    } else if (Array.isArray(value)) {
      result[fullKey] = JSON.stringify(value);
    } else {
      result[fullKey] = value == null ? "" : String(value);
    }
  }
  return result;
}

function jsonToCsv(rows: Record<string, string>[]): string {
  if (rows.length === 0) return "";
  const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const escape = (val: string) => {
    if (val.includes(",") || val.includes('"') || val.includes("\n")) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };
  const headerRow = headers.map(escape).join(",");
  const dataRows = rows.map((row) => headers.map((h) => escape(row[h] ?? "")).join(","));
  return [headerRow, ...dataRows].join("\n");
}

const EXAMPLE_JSON = `[
  {
    "id": 1,
    "name": "Alice",
    "address": {
      "city": "New York",
      "zip": "10001"
    },
    "tags": ["admin", "user"]
  },
  {
    "id": 2,
    "name": "Bob",
    "address": {
      "city": "London",
      "zip": "EC1A 1BB"
    },
    "tags": ["user"]
  }
]`;

const faqItems = [
  {
    question: "What JSON formats are supported?",
    answer:
      "The converter supports JSON arrays of objects, which is the most common format for tabular data. Each object in the array becomes a row in the CSV. The tool automatically discovers all unique keys across all objects, so rows with missing fields will have empty cells for those columns.",
  },
  {
    question: "How does nested object flattening work?",
    answer:
      'Nested objects are flattened using dot notation. For example, a field like {"address": {"city": "New York"}} becomes a column named "address.city" with the value "New York". Arrays are serialized as a JSON string in a single cell, since CSV does not support multi-value fields natively.',
  },
  {
    question: "How are special characters handled?",
    answer:
      "Values containing commas, double quotes, or line breaks are automatically wrapped in double quotes and internal double quotes are escaped (doubled). This follows the RFC 4180 CSV standard, so the output will open correctly in Excel, Google Sheets, and any standard CSV parser.",
  },
  {
    question: "Is there a size limit for the JSON input?",
    answer:
      "There is no hard limit set by this tool — it runs entirely in your browser. In practice, very large JSON files (100MB+) may cause your browser to become slow or unresponsive. For very large datasets, consider splitting them or using a server-side tool like Python's pandas library.",
  },
  {
    question: "Does this tool store my data?",
    answer:
      "No. All processing happens locally in your browser using JavaScript. Your JSON data is never sent to any server. When you close or refresh the page, the data is gone.",
  },
];

export default function JsonToCsvConverterClient() {
  const [jsonInput, setJsonInput] = useState("");
  const [csvOutput, setCsvOutput] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const convert = useCallback(() => {
    setError(null);
    setCsvOutput("");
    setHeaders([]);
    setRows([]);

    if (!jsonInput.trim()) {
      setError("Please enter some JSON to convert.");
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      setError("Invalid JSON. Please check your input and try again.");
      return;
    }

    if (!Array.isArray(parsed)) {
      setError(
        "Input must be a JSON array of objects (e.g. [{...}, {...}]). Single objects are not supported."
      );
      return;
    }

    if (parsed.length === 0) {
      setError("The JSON array is empty — there are no rows to convert.");
      return;
    }

    const flatRows = parsed.map((item) => {
      if (typeof item !== "object" || item === null || Array.isArray(item)) {
        return { value: String(item) };
      }
      return flattenObject(item as Record<string, unknown>);
    });

    const allHeaders = Array.from(new Set(flatRows.flatMap((r) => Object.keys(r))));
    const csv = jsonToCsv(flatRows);
    setHeaders(allHeaders);
    setRows(flatRows);
    setCsvOutput(csv);
  }, [jsonInput]);

  const loadExample = () => {
    setJsonInput(EXAMPLE_JSON);
    setError(null);
    setCsvOutput("");
    setHeaders([]);
    setRows([]);
  };

  const downloadCsv = () => {
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "output.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const reset = () => {
    setJsonInput("");
    setCsvOutput("");
    setHeaders([]);
    setRows([]);
    setError(null);
    setCopied(false);
  };

  const PREVIEW_ROWS = 5;

  return (
    <div className="container px-4 py-12 mx-auto max-w-5xl">
      <ToolPageSchema
        name="JSON to CSV Converter"
        description="Convert JSON arrays to CSV format instantly in your browser. Supports nested objects with dot notation flattening. Preview the table, then download or copy."
        url="https://plugmonkey.xyz/tool/json-to-csv-converter/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "JSON to CSV Converter",
            url: "https://plugmonkey.xyz/tool/json-to-csv-converter/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiTable className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">JSON to CSV Converter</h1>
          <p className="text-muted-foreground mt-1">
            Convert JSON arrays to CSV with nested object flattening. Preview, download, or copy.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Working with JSON data from an API and need it in a spreadsheet? This converter handles it
          entirely in your browser — paste your JSON, hit convert, and get a properly formatted CSV
          file ready to open in Excel or Google Sheets.
        </p>
        <p>
          Nested objects are automatically flattened using dot notation (so{" "}
          <code className="text-xs bg-muted px-1 rounded">address.city</code> becomes a column
          header). Arrays in values are serialized as strings. No data ever leaves your device.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Input */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="json-input" className="text-base font-semibold">
              JSON Input
            </Label>
            <Button variant="ghost" size="sm" onClick={loadExample}>
              Load Example
            </Button>
          </div>
          <Textarea
            id="json-input"
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder='[{"id": 1, "name": "Alice"}, {"id": 2, "name": "Bob"}]'
            className="font-mono text-sm h-64 resize-none"
          />
          <div className="flex gap-2">
            <Button onClick={convert} className="flex-1">
              Convert to CSV
            </Button>
            <Button variant="outline" onClick={reset}>
              <HiRefresh className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* CSV Output */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="csv-output" className="text-base font-semibold">
              CSV Output
            </Label>
            {csvOutput && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                  {copied ? (
                    <HiCheckCircle className="w-4 h-4 mr-1 text-green-500" />
                  ) : (
                    <HiClipboardCopy className="w-4 h-4 mr-1" />
                  )}
                  {copied ? "Copied!" : "Copy"}
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadCsv}>
                  <HiDownload className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </div>
            )}
          </div>
          <Textarea
            id="csv-output"
            name="csv-output"
            readOnly
            value={csvOutput}
            placeholder="CSV output will appear here after conversion..."
            className="font-mono text-sm h-64 resize-none bg-muted/50"
          />
          {csvOutput && (
            <p className="text-xs text-muted-foreground">
              {rows.length} rows, {headers.length} columns
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 mb-6 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Table Preview */}
      {rows.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-base">
              Table Preview{" "}
              {rows.length > PREVIEW_ROWS && (
                <span className="text-muted-foreground font-normal text-sm">
                  (showing {PREVIEW_ROWS} of {rows.length} rows)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-muted">
                    {headers.map((h) => (
                      <th
                        key={h}
                        className="border border-border px-3 py-2 text-left font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, PREVIEW_ROWS).map((row, i) => (
                    <tr key={i} className="even:bg-muted/30">
                      {headers.map((h) => (
                        <td
                          key={h}
                          className="border border-border px-3 py-2 whitespace-nowrap max-w-xs truncate text-muted-foreground"
                        >
                          {row[h] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex gap-3">
              <Button onClick={downloadCsv}>
                <HiDownload className="w-4 h-4 mr-2" />
                Download .csv
              </Button>
              <Button variant="outline" onClick={copyToClipboard}>
                {copied ? (
                  <HiCheckCircle className="w-4 h-4 mr-2 text-green-500" />
                ) : (
                  <HiClipboardCopy className="w-4 h-4 mr-2" />
                )}
                {copied ? "Copied!" : "Copy CSV"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={faqItems} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
